import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Grid, Avatar, Paper, Chip, IconButton, Button, Stack, Divider, Card, CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GroupsIcon from '@mui/icons-material/Groups';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import VerifiedIcon from '@mui/icons-material/Verified';
import FavoriteIcon from '@mui/icons-material/Favorite';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { getUserData } from '../services/storageService';

const useCounter = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const startTime = Date.now();
        const tick = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(start + (end - start) * eased));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration, start]);

  return [count, ref];
};

const About = () => {
  const navigate = useNavigate();
  const user = getUserData();

  const [ordersCount, ordersRef] = useCounter(500000, 2000);
  const [trafficCount, trafficRef] = useCounter(10, 1500);
  const [teamCount, teamRef] = useCounter(50, 1200);

  const backPath = user?.role === 'admin' ? '/admin' : '/dashboard';

  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#0f0f14',
      color: 'white',
      overflowX: 'hidden'
    }}>
      {/* Hero Section */}
      <Box sx={{
        position: 'relative',
        minHeight: { xs: '60vh', md: '70vh' },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          opacity: 0.9,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          height: 120,
          background: 'linear-gradient(to top, #0f0f14, transparent)',
        }
      }}>
        {/* Noise texture overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px',
        }} />

        <IconButton
          onClick={() => navigate(backPath)}
          sx={{
            position: 'absolute', top: 24, left: 24, zIndex: 10,
            color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{
                position: 'relative', display: 'inline-block',
                '&::before': {
                  content: '""', position: 'absolute',
                  top: -12, left: -12, right: -12, bottom: -12,
                  borderRadius: '50%',
                  border: '2px dashed rgba(255,255,255,0.25)',
                  animation: 'spin 25s linear infinite',
                },
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}>
                <Avatar
                  src="/avt2.PNG"
                  alt="Lê Đình Đài"
                  sx={{
                    width: { xs: 160, md: 200 },
                    height: { xs: 160, md: 200 },
                    border: '5px solid rgba(255,255,255,0.9)',
                    boxShadow: '0 16px 60px rgba(0,0,0,0.4)',
                    transition: 'transform 0.5s ease',
                    '&:hover': { transform: 'scale(1.05) rotate(-3deg)' }
                  }}
                >
                  LĐĐ
                </Avatar>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{
                animation: 'fadeUp 0.8s ease-out',
                '@keyframes fadeUp': {
                  from: { opacity: 0, transform: 'translateY(30px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                }
              }}>
                <Chip
                  icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                  label="CEO & Co-Founder"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.18)',
                    color: 'white', fontWeight: 700, mb: 2,
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    fontSize: '0.8rem', height: 32
                  }}
                />
                <Typography variant="h2" sx={{
                  fontWeight: 900, color: 'white', mb: 1,
                  fontSize: { xs: '2.2rem', md: '3.2rem' },
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em'
                }}>
                  Lê Đình Đài
                </Typography>
                <Typography variant="h5" sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontWeight: 400, mb: 3,
                  fontSize: { xs: '1rem', md: '1.3rem' },
                  fontStyle: 'italic'
                }}>
                  "Học hỏi, sáng tạo, làm việc hết mình"
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mt: -6, position: 'relative', zIndex: 5, mb: 8 }}>
        <Grid container spacing={3}>
          {[
            { ref: ordersRef, value: ordersCount, suffix: '+', label: 'Đơn hàng đã xử lý', icon: <StorefrontIcon />, color: '#667eea' },
            { ref: trafficRef, value: trafficCount, suffix: 'M+', label: 'Lượt truy cập', icon: <TrendingUpIcon />, color: '#f093fb' },
            { ref: teamRef, value: teamCount, suffix: '+', label: 'Thành viên đội ngũ', icon: <GroupsIcon />, color: '#764ba2' },
          ].map((stat, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Paper ref={stat.ref} elevation={0} sx={{
                p: 3, borderRadius: 3, textAlign: 'center',
                background: 'linear-gradient(145deg, #1a1a24, #16161e)',
                border: '1px solid rgba(255,255,255,0.06)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  border: `1px solid ${stat.color}40`,
                  boxShadow: `0 20px 40px ${stat.color}15`
                }
              }}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: '14px', mx: 'auto', mb: 1.5,
                  background: `linear-gradient(135deg, ${stat.color}30, ${stat.color}10)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: stat.color
                }}>
                  {stat.icon}
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 0.5 }}>
                  {stat.value.toLocaleString()}{stat.suffix}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* About Carnaval Section */}
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <Box sx={{ mb: 6 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ width: 4, height: 32, borderRadius: 2, background: 'linear-gradient(180deg, #667eea, #764ba2)' }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white' }}>
              Về Carnaval
            </Typography>
          </Stack>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 600, pl: 3, lineHeight: 1.8 }}>
            Carnaval được thành lập với tầm nhìn là một thương hiệu thời trang nữ hiện đại,
            trẻ trung và đầy cá tính. Chúng tôi cam kết mang đến những sản phẩm chất lượng
            với giá cả hợp lý nhất.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {[
            {
              icon: <LiveTvIcon sx={{ fontSize: 28 }} />,
              title: 'Livestream Commerce',
              desc: 'Đội ngũ livestream chuyên nghiệp, tương tác trực tiếp với hàng ngàn khách hàng mỗi ngày trên Shopee và TikTok.',
              color: '#667eea'
            },
            {
              icon: <FavoriteIcon sx={{ fontSize: 28 }} />,
              title: 'Chất lượng là ưu tiên',
              desc: 'Mỗi sản phẩm đều được kiểm tra kỹ lưỡng trước khi đến tay khách hàng, đảm bảo sự hài lòng tuyệt đối.',
              color: '#f093fb'
            },
            {
              icon: <RocketLaunchIcon sx={{ fontSize: 28 }} />,
              title: 'Không ngừng phát triển',
              desc: 'Liên tục đổi mới từ sản phẩm đến quy trình vận hành, ứng dụng công nghệ để tối ưu hiệu suất.',
              color: '#764ba2'
            },
          ].map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card elevation={0} sx={{
                height: '100%', borderRadius: 3,
                background: 'linear-gradient(145deg, #1a1a24, #16161e)',
                border: '1px solid rgba(255,255,255,0.06)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  border: `1px solid ${item.color}40`,
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: '16px', mb: 2,
                    background: `linear-gradient(135deg, ${item.color}25, ${item.color}08)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: item.color
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        py: 4, textAlign: 'center'
      }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
          Carnaval Portal &copy; {new Date().getFullYear()}
        </Typography>
        <Button
          onClick={() => navigate(backPath)}
          sx={{ mt: 1, textTransform: 'none', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}
          startIcon={<ArrowBackIcon />}
        >
          Quay lại Dashboard
        </Button>
      </Box>
    </Box>
  );
};

export default About;
